import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TransportJob {
  id: string;
  order_id: string;
  transporter_company_id: string;
  driver_id: string | null;
  vehicle_id: string | null;
  booked_at: string | null;
  assigned_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  orders?: any;
  drivers?: any;
  vehicles?: any;
}

export const useTransportJobs = () => {
  const queryClient = useQueryClient();

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['transport-jobs'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('transport_jobs')
        .select(`
          *,
          orders (
            order_number,
            cargo_title,
            cargo_description,
            pickup_date,
            delivery_date,
            pickup_city,
            pickup_country,
            pickup_address,
            pickup_lat,
            pickup_lng,
            delivery_city,
            delivery_country,
            delivery_address,
            delivery_lat,
            delivery_lng,
            weight_kg,
            trailer_type,
            proposed_price,
            final_price,
            status,
            created_at
          ),
          drivers (
            user_id,
            users (
              first_name,
              last_name
            )
          ),
          vehicles (
            plate_number,
            brand,
            model
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as TransportJob[];
    },
  });

  const createJobMutation = useMutation({
    mutationFn: async ({ orderId }: { orderId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: userData } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!userData?.company_id) throw new Error('Company not found');

      const { data, error } = await supabase
        .from('transport_jobs')
        .insert({
          order_id: orderId,
          transporter_company_id: userData.company_id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transport-jobs'] });
    },
  });

  const deleteJobMutation = useMutation({
    mutationFn: async (jobId: string) => {
      const { error } = await supabase
        .from('transport_jobs')
        .delete()
        .eq('id', jobId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transport-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-orders'] });
    },
  });

  return {
    jobs,
    isLoading,
    createJob: createJobMutation.mutateAsync,
    isCreating: createJobMutation.isPending,
    deleteJob: deleteJobMutation.mutateAsync,
    isDeleting: deleteJobMutation.isPending,
  };
};
